import { z } from 'zod';
/**
 * Plan General Contable (PGC 2007) — esquemas compartidos.
 *
 * El catálogo de cuentas (Account) clasifica cada cuenta en uno de los 5 grandes
 * grupos contables del PGC: ACTIVO, PASIVO, PATRIMONIO, INGRESO, GASTO. Las
 * cuentas marcadas como `isSystem` son cuentas estructurales del PGC mínimo de
 * JR2 (IVA, capital, retenciones, resultado del ejercicio): no se pueden editar
 * el `name`/`code` ni borrarse; lo único permitido sobre ellas es alternar
 * `active` o ajustar `vatRate` (en cuentas de IVA cuando cambie el tipo legal).
 *
 * `vatRate` se transmite como fracción decimal (0.21 = 21%). Solo aplica a las
 * cuentas relacionadas con IVA (472 soportado, 477 repercutido). En las demás
 * cuentas viaja como null.
 */
export const AccountKindSchema = z.enum([
    'ACTIVO',
    'PASIVO',
    'PATRIMONIO',
    'INGRESO',
    'GASTO',
]);
export const AccountKindLabel = {
    ACTIVO: 'Activo',
    PASIVO: 'Pasivo',
    PATRIMONIO: 'Patrimonio neto',
    INGRESO: 'Ingreso',
    GASTO: 'Gasto',
};
export const AccountDtoSchema = z.object({
    /** Código PGC (3-4 dígitos habitualmente, p.ej. "430", "4751"). */
    code: z.string().min(1),
    name: z.string().min(1),
    kind: AccountKindSchema,
    /** Código de la cuenta padre (p.ej. "47" para 477). */
    parentCode: z.string().nullable(),
    isPostable: z.boolean(),
    /** Cuentas estructurales del PGC: no se pueden editar nombre/código ni borrar. */
    isSystem: z.boolean(),
    active: z.boolean(),
    /** Tipo de IVA aplicable como fracción (0.21 = 21%). Solo cuentas de IVA. */
    vatRate: z.number().nullable(),
});
export const CreateAccountSchema = z.object({
    code: z
        .string()
        .min(1)
        .max(10)
        .regex(/^[0-9]+$/, 'El código debe ser numérico'),
    name: z.string().min(1).max(120),
    kind: AccountKindSchema,
    parentCode: z.string().nullable().optional(),
    vatRate: z.number().min(0).max(1).nullable().optional(),
    active: z.boolean().optional().default(true),
});
export const UpdateAccountSchema = z.object({
    name: z.string().min(1).max(120).optional(),
    active: z.boolean().optional(),
    vatRate: z.number().min(0).max(1).nullable().optional(),
});
// =====================================================================
// Libro diario — JournalEntry y JournalEntryLine
// =====================================================================
export const JournalEntryStatusSchema = z.enum(['BORRADOR', 'ASENTADO', 'ANULADO']);
export const JournalEntryStatusLabel = {
    BORRADOR: 'Borrador',
    ASENTADO: 'Asentado',
    ANULADO: 'Anulado',
};
/** Línea de asiento: cargo XOR abono sobre una cuenta del PGC. Importes en céntimos. */
export const JournalEntryLineDtoSchema = z.object({
    id: z.string(),
    position: z.number().int().min(0),
    accountCode: z.string(),
    /** Nombre de la cuenta (opcional, viene en detalle). */
    accountName: z.string().nullable().optional(),
    description: z.string().nullable(),
    debitCents: z.number().int().min(0),
    creditCents: z.number().int().min(0),
});
/** F1-H — Justificante adjunto a un asiento. */
export const JournalAttachmentDtoSchema = z.object({
    id: z.string(),
    fileName: z.string(),
    mimeType: z.string(),
    /** Bytes. Se serializa como número: los justificantes no llegan a 2^53. */
    size: z.number().int().min(0),
    sha256: z.string(),
    uploadedByName: z.string().nullable(),
    uploadedAt: z.string(),
});
export const JournalAttachmentListResponseSchema = z.object({
    items: z.array(JournalAttachmentDtoSchema),
});
/** Cabecera de asiento. `lines` solo aparece en detalle. */
export const JournalEntryDtoSchema = z.object({
    id: z.string(),
    number: z.number().int(),
    year: z.number().int(),
    entryDate: z.string(),
    description: z.string(),
    status: JournalEntryStatusSchema,
    /** 'invoice_issued' | 'invoice_paid' | 'expense' | 'expense_paid' | 'manual' | 'year_close' | 'year_open' | '<source>_reverse'. */
    source: z.string(),
    invoiceId: z.string().nullable(),
    expenseId: z.string().nullable(),
    reversesId: z.string().nullable(),
    createdById: z.string().nullable(),
    createdAt: z.string(),
    totalDebitCents: z.number().int(),
    totalCreditCents: z.number().int(),
    lines: z.array(JournalEntryLineDtoSchema).optional(),
    /** F1-H — Nº de justificantes adjuntos (va también en el listado, para el clip). */
    attachmentCount: z.number().int().min(0).default(0),
    /** F1-H — Justificantes; solo en el detalle. */
    attachments: z.array(JournalAttachmentDtoSchema).optional(),
});
/** Respuesta paginada del listado de asientos. */
export const JournalEntryListResponseSchema = z.object({
    items: z.array(JournalEntryDtoSchema),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    total: z.number().int().min(0),
});
/** Línea de entrada para POST manual: pierna XOR (debe o haber). */
export const CreateJournalEntryLineSchema = z
    .object({
    accountCode: z.string().min(1),
    debitCents: z.number().int().min(0).default(0),
    creditCents: z.number().int().min(0).default(0),
    description: z.string().max(500).nullable().optional(),
})
    .refine((l) => (l.debitCents > 0) !== (l.creditCents > 0), {
    message: 'Cada línea debe tener exactamente una pierna positiva (debe XOR haber)',
});
/** Cuerpo POST /accounting/journal — solo asientos manuales (source='manual'). */
export const CreateJournalEntrySchema = z
    .object({
    entryDate: z.string().min(1),
    description: z.string().min(1).max(500),
    lines: z.array(CreateJournalEntryLineSchema).min(2),
    status: z.enum(['BORRADOR', 'ASENTADO']).default('BORRADOR'),
})
    .refine((e) => {
    const d = e.lines.reduce((s, l) => s + l.debitCents, 0);
    const c = e.lines.reduce((s, l) => s + l.creditCents, 0);
    return d === c && d > 0;
}, { message: 'El asiento no cuadra: suma debe debe ser igual a suma haber y > 0' });
/** Querystring del listado de asientos. */
export const JournalEntryListQuerySchema = z.object({
    year: z.coerce.number().int().optional(),
    from: z.string().optional(),
    to: z.string().optional(),
    accountCode: z.string().optional(),
    status: JournalEntryStatusSchema.optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
// ============================================================================
// F1 (paridad Ledgeria) — Libro Mayor y Sumas y saldos
// ============================================================================
/**
 * Querystring del Libro Mayor. `accountCode` admite tanto una subcuenta
 * concreta ("43000006" → extracto de ese cliente) como una cuenta madre
 * ("430" → todos los clientes) gracias a `includeChildren`.
 */
export const LedgerQuerySchema = z.object({
    accountCode: z.string().min(1),
    /** Ejercicio; si se omite y no hay from/to, se toma el año en curso. */
    year: z.coerce.number().int().optional(),
    from: z.string().optional(),
    to: z.string().optional(),
    /** true = agrega también las subcuentas que cuelgan del código dado. */
    includeChildren: z.coerce.boolean().default(true),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(500).default(200),
});
/** Un movimiento del Mayor, con el saldo ACUMULADO tras aplicarlo. */
export const LedgerMovementSchema = z.object({
    lineId: z.string(),
    entryId: z.string(),
    entryNumber: z.number().int(),
    entryYear: z.number().int(),
    entryDate: z.string(),
    entryDescription: z.string(),
    accountCode: z.string(),
    accountName: z.string(),
    description: z.string().nullable(),
    debitCents: z.number().int(),
    creditCents: z.number().int(),
    /** Saldo acumulado (deudor positivo) incluyendo el saldo anterior. */
    balanceCents: z.number().int(),
});
export const LedgerResponseSchema = z.object({
    accountCode: z.string(),
    accountName: z.string(),
    includeChildren: z.boolean(),
    /** Saldo acumulado ANTES del primer movimiento del rango. */
    openingBalanceCents: z.number().int(),
    movements: z.array(LedgerMovementSchema),
    /** Totales del RANGO consultado (no incluyen el saldo anterior). */
    totalDebitCents: z.number().int(),
    totalCreditCents: z.number().int(),
    /** Saldo tras el último movimiento del rango. */
    closingBalanceCents: z.number().int(),
    total: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
});
/** Cuenta con movimiento, para el buscador del Mayor. */
export const LedgerAccountOptionSchema = z.object({
    code: z.string(),
    name: z.string(),
    hasMovements: z.boolean(),
});
/** Querystring de Sumas y saldos (balance de comprobación). */
export const TrialBalanceQuerySchema = z.object({
    year: z.coerce.number().int().optional(),
    from: z.string().optional(),
    to: z.string().optional(),
    /**
     * Nivel de agrupación PGC por nº de dígitos (1 = grupo, 2 = subgrupo,
     * 3 = cuenta, 8 = subcuenta). Si se omite, detalle máximo.
     */
    level: z.coerce.number().int().min(1).max(8).optional(),
    /** Oculta las filas cuyo debe, haber y saldo son 0. */
    hideZero: z.coerce.boolean().default(true),
});
export const TrialBalanceRowSchema = z.object({
    code: z.string(),
    name: z.string(),
    debitCents: z.number().int(),
    creditCents: z.number().int(),
    /** debe − haber. Positivo = saldo deudor; negativo = acreedor. */
    balanceCents: z.number().int(),
});
export const TrialBalanceResponseSchema = z.object({
    rows: z.array(TrialBalanceRowSchema),
    totalDebitCents: z.number().int(),
    totalCreditCents: z.number().int(),
    /** El libro cuadra si el total del debe iguala al del haber. */
    balanced: z.boolean(),
    level: z.number().int().nullable(),
});
//# sourceMappingURL=accounting.js.map